import { Type, reflectComponentType } from '@angular/core';

import {
  CellClassParams,
  CellStyle,
  Column,
  ColumnApi,
  GridApi,
  ICellRendererParams,
  IHeaderParams,
  IRowNode,
  SortDirection,
  ValueFormatterParams,
} from 'ag-grid-community';

import { GridContext } from '../models/grid-data';

/**
 * Fabrique les `params` AG Grid consommés par les cell renderers et header components quand ils
 * sont hébergés par `cam-grid-table` (vue tableau custom) et non par AG Grid lui-même.
 *
 * L'objectif est qu'un composant écrit pour AG Grid (`AbstractCellComponent`, `CustomHeaderComponent`,
 * un `cellRenderer` de `fieldsOverrides`…) fonctionne à l'identique dans les deux vues : mêmes
 * `value`, `data`, `node`, `column`, `context.cols`, et pour le header mêmes `setSort` / `showFilter`.
 */

/** Un `cellRenderer` / `headerComponent` est un composant Angular (et non une fonction ou un nom enregistré). */
export function isAngularComponent(renderer: unknown): renderer is Type<unknown> {
  return typeof renderer === 'function' && reflectComponentType(renderer as Type<unknown>) !== null;
}

const noop = () => {
  // Les hooks DOM d'AG Grid (tooltip, row dragger) n'ont pas d'équivalent dans la vue tableau.
};

export function buildTableCellParams<T>(
  api: GridApi<T>,
  column: Column,
  node: IRowNode<T>,
  eGridCell: HTMLElement
): ICellRendererParams<T, unknown, GridContext> {
  const colDef = column.getColDef();
  const context = api.getGridOption('context') as GridContext;
  const columnApi = new ColumnApi(api);

  const formatValue = (value: unknown): string => {
    const formatter = colDef.valueFormatter;
    if (typeof formatter === 'function') {
      const formatterParams: ValueFormatterParams<T, unknown> = {
        api,
        colDef,
        column,
        columnApi,
        context,
        data: node.data,
        node,
        value,
      };
      return formatter(formatterParams) ?? '';
    }
    return value === null || value === undefined ? '' : String(value);
  };

  return {
    api,
    colDef,
    column,
    columnApi,
    context,
    data: node.data,
    eGridCell,
    eParentOfValue: eGridCell,
    formatValue,
    fullWidth: false,
    getValue: () => api.getCellValue({ colKey: column, rowNode: node }),
    node,
    pinned: column.isPinnedLeft() ? 'left' : column.isPinnedRight() ? 'right' : null,
    refreshCell: noop,
    registerRowDragger: noop,
    rowIndex: node.rowIndex ?? 0,
    setTooltip: noop,
    setValue: value => node.setDataValue(column, value),
    value: api.getCellValue({ colKey: column, rowNode: node }),
    valueFormatted: api.getCellValue({ colKey: column, rowNode: node, useFormatter: true }),
  };
}

/** Résout `colDef.cellClass` (chaîne, tableau ou fonction) pour une cellule de la vue tableau. */
export function resolveTableCellClass<T>(api: GridApi<T>, column: Column, node: IRowNode<T>): string[] {
  const cellClass = column.getColDef().cellClass;
  const resolved = typeof cellClass === 'function' ? cellClass(buildCellClassParams(api, column, node)) : cellClass;
  if (!resolved) {
    return [];
  }
  return Array.isArray(resolved) ? resolved : [resolved];
}

/** Résout `colDef.cellStyle` (objet ou fonction) pour une cellule de la vue tableau. */
export function resolveTableCellStyle<T>(api: GridApi<T>, column: Column, node: IRowNode<T>): CellStyle | null {
  const cellStyle = column.getColDef().cellStyle;
  const resolved = typeof cellStyle === 'function' ? cellStyle(buildCellClassParams(api, column, node)) : cellStyle;
  return resolved ?? null;
}

function buildCellClassParams<T>(api: GridApi<T>, column: Column, node: IRowNode<T>): CellClassParams<T, unknown> {
  return {
    api,
    colDef: column.getColDef(),
    column,
    columnApi: new ColumnApi(api),
    context: api.getGridOption('context') as GridContext,
    data: node.data as T,
    node,
    rowIndex: node.rowIndex ?? 0,
    value: api.getCellValue({ colKey: column, rowNode: node }),
  };
}

export function buildTableHeaderParams<T>(
  api: GridApi<T>,
  column: Column,
  eGridHeader: HTMLElement,
  showFilterOverride?: (source: HTMLElement) => void
): IHeaderParams<T, GridContext> {
  const colDef = column.getColDef();
  const filterEnabled = !!colDef.filter;

  const setSort = (sort: SortDirection, multiSort?: boolean) => {
    api.applyColumnState({
      // Sans multi-sort, AG Grid remet les autres colonnes à `null` — même règle ici.
      defaultState: multiSort ? undefined : { sort: null },
      state: [{ colId: column.getColId(), sort }],
    });
  };
  const showFilter = showFilterOverride ?? ((source: HTMLElement) => showTableColumnFilter(api, column, source));

  return {
    api,
    column,
    columnApi: new ColumnApi(api),
    context: api.getGridOption('context') as GridContext,
    displayName: api.getDisplayNameForColumn(column, 'header'),
    eGridHeader,
    enableFilterButton: filterEnabled,
    enableFilterIcon: filterEnabled,
    enableMenu: filterEnabled,
    enableSorting: column.isSortable(),
    progressSort: multiSort => setSort(nextSortDirection(column.getSort()), multiSort),
    setSort,
    setTooltip: noop,
    showColumnMenu: showFilter,
    showColumnMenuAfterMouseClick: event => showFilter(event.target as HTMLElement),
    showFilter,
  };
}

function nextSortDirection(current: SortDirection | undefined): SortDirection {
  switch (current) {
    case 'asc':
      return 'desc';
    case 'desc':
      return null;
    default:
      return 'asc';
  }
}

type MenuServiceLike = {
  showFilterMenu: (params: {
    buttonElement: HTMLElement;
    column: Column;
    containerType: 'columnFilter';
    positionBy: 'button';
  }) => void;
};

/**
 * Ouvre le filtre AG Grid d'une colonne, ancré sur un élément qui n'appartient pas au header AG Grid.
 *
 * L'API publique (`api.showColumnFilter`) ne sait se positionner que sur la cellule de header de la
 * grille — invisible en vue tableau (conteneur à 0px). Le `menuService` interne accepte un
 * `buttonElement` arbitraire ; c'est exactement ce que fait `IHeaderParams.showFilter`. On passe par
 * lui quand il est disponible, avec repli sur l'API publique dépréciée (même ancrage, un warning
 * console unique) si une montée de version AG Grid le renomme.
 */
export function showTableColumnFilter<T>(api: GridApi<T>, column: Column, buttonElement: HTMLElement) {
  const menuService = (api as unknown as { menuService?: Partial<MenuServiceLike> }).menuService;
  if (typeof menuService?.showFilterMenu === 'function') {
    menuService.showFilterMenu({ buttonElement, column, containerType: 'columnFilter', positionBy: 'button' });
    return;
  }
  api.showColumnMenuAfterButtonClick(column, buttonElement);
}
